// src/features/Learner/CatalogView.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import './CatalogView.css';
import Leaderboard from './components/Leaderboard';
import ScheduleTable from './components/ScheduleTable';

// ---------------------------------------------------------------------------
// LessonCard
// ---------------------------------------------------------------------------
const LessonCard = React.memo(({ lesson, isVisited, progress, userProgress, onNavigate }) => {
    const handleClick = useCallback(() => onNavigate(lesson.id), [lesson.id, onNavigate]);

    const getProgressInfo = () => {
        if (userProgress && userProgress.visitedCount === userProgress.totalNavItems
                && userProgress.totalNavItems > 0) {
            return { dot: '#19a4a0', status: 'completed' };
        } else if (isVisited) {
            return { dot: '#e8a327', status: 'in-progress' };
        }
        return { dot: null, status: 'available' };
    };

    const progressInfo = getProgressInfo();

    return (
        <div
            className={`lesson-card ${progressInfo.status}`}
            onClick={handleClick}
            style={{
                backgroundColor: '#ffffff',
                borderRadius: '10px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                cursor: 'pointer',
                marginBottom: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}
        >
            {/* Row 1: status dot + title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 12px 4px' }}>
                <span style={{
                    width: '14px', height: '14px', borderRadius: '50%',
                    backgroundColor: progressInfo.dot || 'transparent',
                    border: progressInfo.dot ? 'none' : '2px solid #adb5bd',
                    flexShrink: 0, display: 'inline-block'
                }} />
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                    {lesson.title}
                </h3>
            </div>

                 {/* Row 3: lesson image */}
            <div style={{ margin: '0 12px 8px', borderRadius: '8px', border: '2px solid #19a4a0', overflow: 'hidden', lineHeight: 0 }}>
                <img
                    src={lesson.imageUrl || '/assets/default-lesson.jpg'}
                    alt={lesson.title}
                    style={{ width: '100%', height: 'auto', objectFit: 'unset', display: 'block' }}
                />
            </div>

            {/* Row 4: progress bar — only if started */}
            {userProgress && userProgress.visitedCount > 0 && (
                <div style={{ padding: '0 12px 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1, height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                            height: '100%', width: `${progress}%`,
                            backgroundColor: '#19a4a0', borderRadius: '3px', transition: 'width 0.3s ease'
                        }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', flexShrink: 0 }}>{progress}%</span>
                </div>
            )}

            {/* Row 5: objective */}
            {lesson.lessonObjective && (
                <div style={{ padding: '0 12px 12px', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic', lineHeight: 1.4 }}>
                    {lesson.lessonObjective}
                </div>
            )}
        </div>
    );
});

LessonCard.displayName = 'LessonCard';

// ---------------------------------------------------------------------------
// CatalogView
// ---------------------------------------------------------------------------
const CatalogView = () => {
    const { catalogId } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [catalog, setCatalog] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [userProgress, setUserProgress] = useState({});  // keyed by lessonId
    const [enrolledAt, setEnrolledAt] = useState(null);     // course start date, from userCatalogs
    const [tutorConfirmedLessonIds, setTutorConfirmedLessonIds] = useState(new Set());
    const [enrollmentId, setEnrollmentId] = useState(null); // userCatalogs doc id, needed to write custom dates
    const [customLessonDates, setCustomLessonDates] = useState({}); // lessonId -> Timestamp, self-set overrides
    const [isIndividualLearner, setIsIndividualLearner] = useState(false); // cohort === 'Other' or none
    const [showSchedule, setShowSchedule] = useState(false);
    const [error, setError] = useState(null);

    // Derived helpers
    const isLessonVisited = useCallback((lessonId) =>
        (userProgress[lessonId]?.visitedCount || 0) > 0, [userProgress]);

    const isLessonCompleted = useCallback((lessonId) => {
        const p = userProgress[lessonId];
        return p && p.totalNavItems > 0 && p.completedCount >= p.totalNavItems;
    }, [userProgress]);

    const getLessonProgress = useCallback((lessonId) => {
        const p = userProgress[lessonId];
        if (!p || p.totalNavItems === 0) return 0;
        return Math.round((p.completedCount / p.totalNavItems) * 100);
    }, [userProgress]);

    const navigateToLesson = useCallback((lessonId) =>
        navigate(`/learn/lesson/${lessonId}`), [navigate]);

    const navigateToDashboard = useCallback(() =>
        navigate('/learn/dashboard'), [navigate]);

    const continueLearning = useCallback(() => {
        const incomplete = lessons.find(l => !isLessonCompleted(l.id));
        navigateToLesson(incomplete ? incomplete.id : lessons[lessons.length - 1]?.id);
    }, [lessons, isLessonCompleted, navigateToLesson]);

    const progressStats = useMemo(() => {
        const total = lessons.length;
        const visited = lessons.filter(l => isLessonVisited(l.id)).length;
        const completed = lessons.filter(l => isLessonCompleted(l.id)).length;
        return {
            total, visited, completed,
            percentage: total > 0 ? Math.round((visited / total) * 100) : 0
        };
    }, [lessons, isLessonVisited, isLessonCompleted]);

    // ---------------------------------------------------------------------------
    // Main data fetch — reads only:
    //   1. userCatalogs access check  (1 query)
    //   2. catalog doc                (1 read)
    //   3. lessons for catalog        (1 query)
    //   4. learnerProgress doc        (1 read)
    //
    // visitedCount per lesson is computed from visitedPackages (stored on
    // the progress doc) by checking which lesson each package belongs to.
    // lessonTotals (written by useUnitNavigation on first open) gives us the
    // denominator without any extra reads.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        const fetchData = async () => {
            if (!currentUser || !catalogId) return;

            try {
                setLoading(true);

                // Access check + catalog doc + lessons + progress + tutor sessions + own user doc,
                // in parallel. The user doc gives us cohort, which decides whether this learner
                // gets the fixed funded-cohort schedule or the self-editable individual one.
                const [userCatalogsSnapshot, catalogDoc, lessonsSnapshot, progressSnap, tutorSessionsSnapshot, ownUserDoc] = await Promise.all([
                    getDocs(query(
                        collection(db, 'userCatalogs'),
                        where('userId', '==', currentUser.uid),
                        where('catalogId', '==', catalogId)
                    )),
                    getDoc(doc(db, 'catalogs', catalogId)),
                    getDocs(query(
                        collection(db, 'lessons'),
                        where('catalogId', '==', catalogId),
                        orderBy('order', 'asc')
                    )),
                    getDoc(doc(db, 'learnerProgress', currentUser.uid)),
                    getDocs(query(
                        collection(db, 'tutoring_sessions'),
                        where('learner_id', '==', currentUser.uid),
                        where('status', '==', 'completed')
                    )),
                    getDoc(doc(db, 'users', currentUser.uid))
                ]);

                if (userCatalogsSnapshot.empty) {
                    setError('You do not have access to this catalog.');
                    return;
                }
                if (!catalogDoc.exists()) {
                    setError('Catalog not found.');
                    return;
                }

                setCatalog({ id: catalogDoc.id, ...catalogDoc.data() });
                // Course start date — drives the schedule table's week numbering and pace check.
                const enrollmentDoc = userCatalogsSnapshot.docs[0];
                setEnrolledAt(enrollmentDoc?.data()?.enrolledAt || null);
                setEnrollmentId(enrollmentDoc?.id || null);
                setCustomLessonDates(enrollmentDoc?.data()?.customLessonDates || {});

                // Funded/cohort learners get the fixed, admin-paced schedule. Individual
                // learners (cohort "Other", or no cohort at all) aren't on a funded timeline,
                // so they get a self-editable version instead — see ScheduleTable.
                const cohort = ownUserDoc.exists() ? ownUserDoc.data().cohort : null;
                setIsIndividualLearner(!cohort || cohort === 'Other');

                if (lessonsSnapshot.empty) {
                    setLessons([]);
                    return;
                }

                const lessonsData = lessonsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setLessons(lessonsData);

                // ---------------------------------------------------------------------------
                // Build progress map using completedNavItems (fully teal) for the
                // progress bar, and visitedPackages for the visited dot on lesson cards.
                // ---------------------------------------------------------------------------
                const progressData = progressSnap.exists() ? progressSnap.data() : {};

                // completedNavItems: map of navItemId -> { lessonId, completedAt }
                const rawCompleted = progressData.completedNavItems || {};
                const completedNavItemsSet = new Set(Object.keys(rawCompleted));

                // visitedPackages: kept for the yellow visited dot on lesson cards
                const rawPackages = progressData.visitedPackages;
                let visitedPackagesSet;
                if (Array.isArray(rawPackages)) {
                    visitedPackagesSet = new Set(rawPackages);
                } else if (rawPackages && typeof rawPackages === 'object') {
                    visitedPackagesSet = new Set(Object.keys(rawPackages));
                } else {
                    visitedPackagesSet = new Set();
                }

                const lessonTotals = progressData.lessonTotals || {};

                // Fetch package and standalone unit IDs per lesson
                const lessonIds = lessonsData.map(l => l.id);
                const packageMetaSnapshot = await getDocs(query(
                    collection(db, 'packages'),
                    where('lessonId', 'in', lessonIds)
                ));

                const packagesByLesson = {};
                packageMetaSnapshot.docs.forEach(d => {
                    const lessonId = d.data().lessonId;
                    if (!packagesByLesson[lessonId]) packagesByLesson[lessonId] = [];
                    packagesByLesson[lessonId].push(d.id);
                });

                const unitMetaSnapshot = await getDocs(query(
                    collection(db, 'units'),
                    where('lesson_id', 'in', lessonIds)
                ));

                const unitIdsInPackages = new Set();
                packageMetaSnapshot.docs.forEach(d => {
                    (d.data().unitIds || []).forEach(uid => unitIdsInPackages.add(uid));
                });

                const standalonesByLesson = {};
                unitMetaSnapshot.docs.forEach(d => {
                    if (!unitIdsInPackages.has(d.id)) {
                        const lid = d.data().lesson_id;
                        if (!standalonesByLesson[lid]) standalonesByLesson[lid] = [];
                        standalonesByLesson[lid].push(d.id);
                    }
                });

                const visitedUnitsSet = new Set(progressData.visitedUnits || []);

                // ---------------------------------------------------------------------------
                // Tutor confirmation per lesson: a lesson is "tutor confirmed" once ANY of its
                // units has a completed tutoring_sessions doc for this learner (units are
                // discussion topics — tutors only cover a subset per lesson, and one completed
                // session on any of them is enough; it's not required to cover every unit).
                // ---------------------------------------------------------------------------
                const lessonIdByUnitId = {};
                unitMetaSnapshot.docs.forEach(d => {
                    lessonIdByUnitId[d.id] = d.data().lesson_id;
                });
                const tutorConfirmedLessons = new Set();
                tutorSessionsSnapshot.docs.forEach(d => {
                    const lessonId = lessonIdByUnitId[d.data().unit_id];
                    if (lessonId) tutorConfirmedLessons.add(lessonId);
                });
                setTutorConfirmedLessonIds(tutorConfirmedLessons);

                const lessonProgress = {};
                lessonsData.forEach(lesson => {
                    const totalNavItems = lessonTotals[lesson.id] || 0;
                    const pkgIds = packagesByLesson[lesson.id] || [];
                    const standaloneIds = standalonesByLesson[lesson.id] || [];

                    // visitedCount: any visit — drives the yellow/teal dot on lesson card
                    const visitedPkgs = pkgIds.filter(id => visitedPackagesSet.has(id)).length;
                    const visitedStandalone = standaloneIds.filter(id => visitedUnitsSet.has(id)).length;
                    const visitedCount = visitedPkgs + visitedStandalone;

                    // completedCount: fully teal nav items only — drives progress bar %
                    const completedPkgs = pkgIds.filter(id => completedNavItemsSet.has(id)).length;
                    const completedStandalone = standaloneIds.filter(id => completedNavItemsSet.has(id)).length;
                    const completedCount = completedPkgs + completedStandalone;

                    lessonProgress[lesson.id] = { totalNavItems, visitedCount, completedCount };
                });

                setUserProgress(lessonProgress);

            } catch (err) {
                console.error('Error fetching catalog data:', err);
                setError('An error occurred while loading the catalog.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser, catalogId]);

    const memoizedLessonCards = useMemo(() =>
        lessons.map(lesson => (
            <LessonCard
                key={lesson.id}
                lesson={lesson}
                isVisited={isLessonVisited(lesson.id)}
                progress={getLessonProgress(lesson.id)}
                userProgress={userProgress[lesson.id]}
                onNavigate={navigateToLesson}
            />
        )),
        [lessons, userProgress, isLessonVisited, getLessonProgress, navigateToLesson]
    );

    if (loading) {
        return (
            <div className="loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading catalog...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-icon">❌</div>
                <p className="error-message">{error}</p>
                <button className="back-button" onClick={navigateToDashboard}>← Back to Dashboard</button>
            </div>
        );
    }

    return (
        <div className="catalog-view">

            {/* Top row: back + continue */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                <button
                    onClick={navigateToDashboard}
                    aria-label="Back to Dashboard"
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '36px', height: '36px', borderRadius: '50%', border: 'none',
                        backgroundColor: '#e8a327', color: '#ffffff', fontSize: '16px',
                        cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent'
                    }}
                >&#9664;</button>

                {lessons.length > 0 && (
                    <button
                        onClick={continueLearning}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            height: '36px', paddingLeft: '16px', paddingRight: '16px',
                            borderRadius: '18px', border: 'none', backgroundColor: '#19a4a0',
                            color: '#ffffff', fontSize: '0.9rem', fontWeight: 600,
                            cursor: 'pointer', WebkitTapHighlightColor: 'transparent'
                        }}
                    >&#9654; Continue Learning</button>
                )}
            </div>

            {/* Catalog title + visited count */}
            {catalog && (
                <div style={{ marginBottom: '1.5rem' }}>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#19a4a0', margin: '0 0 4px' }}>
                        {catalog.title}
                    </h1>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        👁️ {progressStats.visited}/{progressStats.total} lessons visited
                    </span>
                </div>
            )}

            {/* ---------------------------------------------------------------------------
                Leaderboard — only shown when admin has enabled it for this catalog.
                Add leaderboardEnabled: true to the catalog document in Firestore
                to turn it on. No code change needed per catalog.
            --------------------------------------------------------------------------- */}
            {catalog?.leaderboardEnabled && (
                <Leaderboard catalogId={catalogId} />
            )}

            {/* ---------------------------------------------------------------------------
                Schedule — collapsed by default to keep the header light. Funded/cohort
                learners get the fixed pace plan; individual learners (no cohort, or
                cohort "Other") get self-editable target dates instead — see ScheduleTable.
                Only makes sense once enrolledAt is known; if it's missing (e.g. an old
                enrollment from before this field existed), skip it rather than show a
                broken week count.
            --------------------------------------------------------------------------- */}
            {enrolledAt && lessons.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                    <button
                        onClick={() => setShowSchedule(s => !s)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                            padding: '10px 14px', fontSize: '0.85rem', fontWeight: 500,
                            color: '#19a4a0', backgroundColor: '#f0fdfa', border: '1px solid #ccfbf1',
                            borderRadius: '10px', cursor: 'pointer',
                        }}
                    >
                        <span>{showSchedule ? '▲' : '▾'}</span>
                        {showSchedule ? 'Hide schedule' : 'Show schedule'}
                    </button>
                    {showSchedule && (
                        <div style={{ marginTop: '10px' }}>
                            <ScheduleTable
                                lessons={lessons}
                                userProgress={userProgress}
                                enrolledAt={enrolledAt}
                                tutorConfirmedLessonIds={tutorConfirmedLessonIds}
                                isIndividualLearner={isIndividualLearner}
                                enrollmentId={enrollmentId}
                                initialCustomLessonDates={customLessonDates}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Lessons */}
            <div className="lessons-section">
                <h2>📝 Lessons</h2>
                {lessons.length === 0 ? (
                    <div className="no-lessons">
                        <div className="no-lessons-icon">📭</div>
                        <p>No lessons found in this catalog.</p>
                    </div>
                ) : (
                    <div className="lessons-grid">{memoizedLessonCards}</div>
                )}
            </div>
        </div>
    );
};

export default React.memo(CatalogView);